import { useQuery } from "react-query";
import { getDisplayFeedbackDialog } from "api/feedback";
import { GET_DISPLAY_FEEDBACK_DIALOG } from "@constants/reactQueryKeys";

/**
 * Hook to check if the feedback dialog should be displayed
 * 
 * Fetches a boolean gate value from the backend to determine if the user
 * should see the feedback dialog. Defaults to false on error.
 * 
 * @returns {Object} Object with shouldDisplayDialog boolean and triggerGateCheck function
 * 
 * @example
 * const { shouldDisplayDialog, triggerGateCheck } = useGetDisplayFeedbackDialog();
 * 
 * // Trigger the gate check manually
 * triggerGateCheck();
 * 
 * if (shouldDisplayDialog) {
 *   // Show feedback dialog
 * }
 */
export const useGetDisplayFeedbackDialog = () => {
  const { data, isError, refetch } = useQuery(
    [GET_DISPLAY_FEEDBACK_DIALOG],
    getDisplayFeedbackDialog,
    {
      enabled: false,
      staleTime: Infinity,
      onError: (error: Error) => {
        console.error("Error fetching feedback dialog gate:", error);
      },
    }
  );

  if (isError) {
    return {
      shouldDisplayDialog: false,
      triggerGateCheck: refetch,
    };
  }

  return {
    shouldDisplayDialog: data?.data?.result ?? false,
    triggerGateCheck: refetch,
  };
};